import { useState } from 'react';
import { audio } from '../audio/AudioEngine.ts';
import { MOONLIGHT_START } from '../sim/constants.ts';
import { FailReason } from '../sim/types.ts';
import type { HudState } from './useGame.ts';

/**
 * What the player sees on failure, keyed by the real cause.
 *
 * Pillar 5 (brief §2) is that the player must always be able to answer "what
 * killed me". A single hardcoded "Out of moonlight" string used to render for
 * every failure, chimera contact and falling into the void included — found
 * while testing the Warden, the first room content with a reachable contact
 * death, since no room before it made the gap between message and cause
 * observable.
 */
const FAIL_MESSAGE: Record<string, string> = {
  [FailReason.Moonlight]: 'Out of moonlight',
  [FailReason.Chimera]: 'A Chimera caught you',
  [FailReason.Void]: 'You fell',
};

/**
 * Minimal HUD. Every audio cue in the game has a visual counterpart, and the
 * low-moonlight warning is one of them (brief §11, §13).
 */
export function Hud({
  hud,
  onRestart,
  onReturnToMap,
  onOpenSanctum,
  assistEnabled,
  showAssistOffer,
  onToggleAssist,
}: {
  hud: HudState;
  onRestart: () => void;
  /** Omitted when the room was launched via ?room=, which has no map to return to. */
  onReturnToMap?: () => void;
  /** Opens the Sanctum overlay (brief §8/§10: reachable from Pause). */
  onOpenSanctum?: () => void;
  assistEnabled: boolean;
  /** Shown after three failed attempts, without hiding the pause-menu toggle. */
  showAssistOffer: boolean;
  onToggleAssist: () => void;
}) {
  // `audio` is a session-wide singleton (brief §13), not room state - a
  // plain local mirror is enough since these two buttons are the only thing
  // that ever changes them. Separate toggles (direct request): a player who
  // wants the ambient pad off but still wants to hear cast/land/pickup SFX
  // (or vice versa) couldn't do that with one combined mute.
  const [sfxMuted, setSfxMuted] = useState(() => audio.isSfxMuted());
  const [musicMuted, setMusicMuted] = useState(() => audio.isMusicMuted());
  const pct = Math.max(0, Math.min(1, hud.moonlight / MOONLIGHT_START));
  const low = hud.moonlight < 15;
  // A room with no items at all is never one of the eight puzzles - only the
  // Grove hub (see `buildGroveHub`) fits that, and it also has no real time
  // pressure (its moonlight budget is a large placeholder, not a countdown
  // anyone is meant to watch).
  const isPracticeRoom = hud.itemsTotal === 0;

  return (
    <div className="hud">
      <div className="hud-row">
        <span className="hud-room">{hud.roomName}</span>
        <span className="hud-row-right">
          <button
            type="button"
            className={`mute-btn${musicMuted ? ' is-muted' : ''}`}
            onClick={() => setMusicMuted(audio.toggleMusicMute())}
            aria-label={musicMuted ? 'Unmute music' : 'Mute music'}
            aria-pressed={musicMuted}
          >
            🎵
          </button>
          <button
            type="button"
            className="mute-btn"
            onClick={() => setSfxMuted(audio.toggleSfxMute())}
            aria-label={sfxMuted ? 'Unmute sound effects' : 'Mute sound effects'}
            aria-pressed={sfxMuted}
          >
            {sfxMuted ? '🔇' : '🔊'}
          </button>
          <span className={`hud-moon${low ? ' is-low' : ''}`}>
            {isPracticeRoom ? '∞' : `${hud.moonlight.toFixed(1)}s`}
          </span>
        </span>
      </div>
      <div className="moonbar" role="progressbar" aria-valuemin={0} aria-valuemax={MOONLIGHT_START} aria-valuenow={Math.round(hud.moonlight)} aria-label="Moonlight remaining">
        <div className={`moonbar-fill${low ? ' is-low' : ''}`} style={{ width: `${pct * 100}%` }} />
      </div>
      <div className="hud-row hud-sub">
        {isPracticeRoom ? (
          <span>Cast blocks to reach a door</span>
        ) : (
          <>
            <span>{hud.portalPrompt ? 'Portal ready — press E to enter' : hud.hasMoonseed ? 'Moonseed taken — portal open' : 'Find the Moonseed'}</span>
            <span>
              {hud.itemsTaken}/{hud.itemsTotal}
            </span>
          </>
        )}
      </div>

      {!hud.paused && hud.countdown !== null && (
        <div className="overlay">
          <p>{Math.ceil(hud.countdown)}</p>
          <p className="overlay-sub">Get ready</p>
        </div>
      )}
      {hud.paused && (
        <div className="overlay">
          <p>Paused</p>
          <p className="overlay-sub">Esc or P to resume</p>
          {onOpenSanctum && (
            <div className="overlay-actions">
              <button type="button" onClick={onOpenSanctum}>
                Sanctum
              </button>
            </div>
          )}
          <div className="overlay-actions">
            <button type="button" onClick={onToggleAssist}>
              Assist Mode: {assistEnabled ? 'On' : 'Off'}
            </button>
          </div>
        </div>
      )}
      {hud.countdown === null && hud.doorPrompt && (
        <p className={`door-prompt${hud.doorPrompt.locked ? ' is-locked' : ''}`}>
          {hud.doorPrompt.locked
            ? `Door ${hud.doorPrompt.label} — Locked`
            : `Press E to enter Door ${hud.doorPrompt.label}`}
        </p>
      )}
      {hud.countdown === null && hud.portalPrompt && (
        <p className="door-prompt">Press E to enter the Portal</p>
      )}
      {hud.outcome === 'cleared' && (
        <div className="overlay">
          <p>Room cleared</p>
          <div className="overlay-actions">
            <button type="button" onClick={onRestart}>
              Play again
            </button>
            {onReturnToMap && (
              <button type="button" onClick={onReturnToMap}>
                Return to Grove
              </button>
            )}
          </div>
        </div>
      )}
      {hud.outcome === 'failed' && (
        <div className="overlay">
          <p>{FAIL_MESSAGE[hud.failReason] ?? 'Room failed'}</p>
          {showAssistOffer && (
            <p className="overlay-sub">Want more time? Assist Mode slows moonlight drain by half.</p>
          )}
          {showAssistOffer && (
            <button type="button" onClick={onToggleAssist}>
              Turn on Assist Mode
            </button>
          )}
          <button type="button" onClick={onRestart}>
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
